import { useState, useEffect, useMemo } from 'react';

const useMapForm = (isEditMode, selectedData = [], rolesOption = [], isRemoveMode = false) => {
  const [roleName, setRoleName] = useState([]);
  const [roleError, setRoleError] = useState('');

  // Extract array of role name strings for a single user row
  const getUserRoleNames = (userRow) => {
    if (!userRow?.roleName || userRow.roleName === 'NA') return [];
    return String(userRow.roleName)
      .split(',')
      .map((roleItem) => roleItem.trim())
      .filter(Boolean);
  };

  // Compute common roles that EVERY selected user possesses
  const disabledRoleOptions = useMemo(() => {
    if (isRemoveMode) return []; // In remove mode, nothing is disabled
    if (!selectedData || selectedData.length === 0 || !rolesOption.length) return [];

    if (selectedData.length === 1) {
      // Single selection: disable all existing roles of that user
      const existingNames = getUserRoleNames(selectedData[0]);
      return rolesOption.filter((opt) => existingNames.includes(opt.name));
    }

    // Bulk selection: disable ONLY roles that ALL selected users share
    const allUsersRoles = selectedData.map((user) => getUserRoleNames(user));
    return rolesOption.filter((opt) =>
      allUsersRoles.every((userRoles) => userRoles.includes(opt.name)),
    );
  }, [selectedData, rolesOption, isRemoveMode]);

  // Compute union of roles across selected users for Remove Role modal
  const removeRoleOptions = useMemo(() => {
    if (!isRemoveMode || !selectedData || selectedData.length === 0 || !rolesOption.length) return [];
    const allRoleNames = new Set(selectedData.flatMap((user) => getUserRoleNames(user)));
    return rolesOption.filter((opt) => allRoleNames.has(opt.name));
  }, [selectedData, rolesOption, isRemoveMode]);

  useEffect(() => {
    if (!selectedData || selectedData.length === 0) {
      setRoleName([]);
      return;
    }

    if (isRemoveMode) {
      // Remove Role modal: pre-select all assigned roles (union across selected users)
      setRoleName(removeRoleOptions);
      return;
    }

    if (isEditMode) {
      const getInitialRoleArray = (roleValue) => {
        return roleValue.length > 0 && typeof roleValue[0] === 'string'
          ? roleValue[0].split(',').map((value) => value.trim())
          : [];
      };

      if (selectedData[0]?.roleName === 'NA') {
        setRoleName([]);
        return;
      }

      const initialRoleArray = getInitialRoleArray([selectedData[0]?.roleName]);
      const filteredRole = initialRoleArray
        .map((role) => rolesOption.find((option) => option.name === role))
        .filter(Boolean);

      setRoleName(filteredRole);
    } else {
      // Non-edit mode (Map Role modal): pre-select the disabled (common) roles
      setRoleName(disabledRoleOptions);
    }
  }, [isEditMode, selectedData, rolesOption, disabledRoleOptions, isRemoveMode, removeRoleOptions]);

  const resetValues = () => {
    setRoleName([]);
    setRoleError('');
  };

  return {
    roleName,
    setRoleName,
    roleError,
    setRoleError,
    resetValues,
    disabledRoleOptions,
    removeRoleOptions,
  };
};

export default useMapForm;
